import { Button } from '@/components/Button';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Check, Gift, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePage, router } from '@inertiajs/react';
import { useConfirm } from '@/hooks/useConfirm';

export function RedeemModal({ isOpen, onClose, amount, pointsCost }) {
  const { auth } = usePage().props;
  const user = auth?.user;
  const { confirm, ConfirmDialog } = useConfirm();

  const [step, setStep] = useState('confirm'); // 'confirm' | 'success'
  const [loading, setLoading] = useState(false);
  const [localAmount, setLocalAmount] = useState(amount);

  const pointsPerPeso = amount > 0 ? pointsCost / amount : 10;
  const localPointsCost = localAmount * pointsPerPeso;
  const REDEEMABLE_POINTS = user?.points_balance || 0;

  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setLoading(false);
      setLocalAmount(amount);
    }
  }, [isOpen, amount]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const GIFT_CARD = { id: 'gift_card', name: 'Tarjeta de Regalo', icon: '🎁' };

  const handleConfirm = () => {
    if (!user) return;
    setLoading(true);

    router.post('/rewards/redeem', {
      amount: localAmount,
      points_cost: localPointsCost,
      brand_id: GIFT_CARD.id,
      brand_name: GIFT_CARD.name
    }, {
      preserveState: false,
      preserveScroll: true,
      onSuccess: () => {
        setStep('success');
        setLoading(false);
        setTimeout(() => {
          onClose();
        }, 3000);
      },
      onError: async (errors) => {
        console.error("Redemption Error:", errors);
        await confirm({
          title: 'Error al canjear',
          message: 'Hubo un error al procesar tu canje.',
          confirmText: 'Entendido',
          cancelText: '',
          variant: 'danger'
        });
        setLoading(false);
      }
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-montserrat font-bold text-lg text-gray-800 flex items-center gap-2">
                <Gift className="w-5 h-5 text-bambu-green" />
                {step === 'success' ? '¡Canje exitoso!' : 'Confirmar canje'}
              </h3>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto">

              {step === 'confirm' && (
                <>
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 mx-auto mb-4 bg-green-50 rounded-full flex items-center justify-center">
                      <span className="text-4xl">{GIFT_CARD.icon}</span>
                    </div>
                    <p className="text-gray-500 font-medium mb-1">Valor a canjear</p>
                    <div className="flex items-center justify-center gap-4 mt-2">
                      <button
                        onClick={() => setLocalAmount(Math.max(100, localAmount - 100))}
                        disabled={localAmount <= 100}
                        className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 text-xl font-bold hover:bg-gray-200 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        -
                      </button>
                      <div className="text-center min-w-30">
                        <div className="text-4xl font-black text-gray-900">${localAmount} <span className="text-lg font-bold text-gray-400">MXN</span></div>
                      </div>
                      <button
                        onClick={() => setLocalAmount(localAmount + 100)}
                        disabled={(localAmount + 100) * pointsPerPeso > REDEEMABLE_POINTS}
                        className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 text-xl font-bold hover:bg-gray-200 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        +
                      </button>
                    </div>
                    <div className="text-sm font-bold text-bambu-green mt-2 bg-bambu-green/10 inline-block px-3 py-1 rounded-full">
                      Costo: {localPointsCost} pts
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">Tipo de recompensa:</span>
                      <span className="text-sm font-bold text-gray-900">{GIFT_CARD.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Balance actual:</span>
                      <span className="text-sm font-bold text-gray-900">{user?.points_balance || 0} pts</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-500">Balance después:</span>
                      <span className="text-sm font-bold text-bambu-green">{(user?.points_balance || 0) - localPointsCost} pts</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
                    <p className="text-sm text-blue-900">
                      <strong>📋 Proceso:</strong> Tu solicitud será procesada y recibirás la notificación en Basecamp.
                    </p>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Button
                      onClick={handleConfirm}
                      disabled={loading}
                      className="w-full py-4 text-base rounded-xl shadowing-lg flex items-center justify-center gap-2"
                    >
                      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loading ? 'Procesando...' : `Confirmar canje (${localPointsCost} pts)`}
                    </Button>
                  </div>
                </>
              )}

              {step === 'success' && (
                <div className="py-12 flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 animate-bounce">
                    <Check className="w-10 h-10 text-green-600 stroke-[3]" />
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 mb-2">¡Solicitud Enviada!</h2>
                  <p className="text-gray-500 max-w-xs mx-auto mb-8">
                    Tu <strong>{GIFT_CARD.name}</strong> por <strong>${localAmount} MXN</strong> será procesada pronto. Recibirás la notificación en Basecamp.
                  </p>
                  <div className="bg-gray-50 rounded-xl p-4 w-full max-w-sm border border-gray-100">
                    <div className="flex justify-between items-center text-sm mb-2">
                      <span className="text-gray-500">Nuevo balance:</span>
                      <span className="font-bold text-gray-900">{(user?.points_balance || 0) - localPointsCost} pts</span>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </motion.div>
        </div>
      )}

      <ConfirmDialog />
    </AnimatePresence>
  );
}
